import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { resolveTenantId } from '../utils/tenantUtils'
import { useWallet } from '../contexts/WalletContext'
import * as casinoService from '../services/casinoService'
import type { Game, PopularGame, Provider } from '../services/casinoService'

type CategoryId = 'all' | 'casino' | 'slot' | 'poker' | 'fishing' | 'sport' | 'popular'

const ALL_PROVIDERS_ID = '__all__'
const PLACEHOLDER_IMAGE =
  'data:image/svg+xml;charset=UTF-8,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 420">
      <defs>
        <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stop-color="#0f172a"/>
          <stop offset="100%" stop-color="#1d4ed8"/>
        </linearGradient>
      </defs>
      <rect width="320" height="420" fill="url(#g)"/>
      <text x="50%" y="48%" dominant-baseline="middle" text-anchor="middle" fill="#ffffff" font-size="28" font-family="Arial, sans-serif">CASINO</text>
      <text x="50%" y="58%" dominant-baseline="middle" text-anchor="middle" fill="rgba(255,255,255,.65)" font-size="16" font-family="Arial, sans-serif">GAME</text>
    </svg>`,
  )

const CATEGORY_DEFS: Array<{
  id: CategoryId
  label: string
  icon: string
  ambCategory?: string
}> = [
  { id: 'all', label: 'ทั้งหมด', icon: 'fas fa-th-large' },
  { id: 'slot', label: 'สล็อต', icon: 'fas fa-dice', ambCategory: 'EGAMES' },
  { id: 'casino', label: 'คาสิโน', icon: 'fas fa-chess-rook', ambCategory: 'LIVECASINO' },
  { id: 'poker', label: 'ป๊อกเด้ง', icon: 'fas fa-clone', ambCategory: 'POKER' },
  { id: 'fishing', label: 'ยิงปลา', icon: 'fas fa-fish', ambCategory: 'KENO' },
  { id: 'sport', label: 'กีฬา', icon: 'fas fa-futbol', ambCategory: 'SPORT' },
  { id: 'popular', label: 'ยอดนิยม', icon: 'fas fa-fire' },
]

interface ActiveGame {
  url: string
  gameName: string
  providerName: string
  gameCode: string
  providerId: string
  openedInNewTab?: boolean
}

function Spinner() {
  return (
    <div className="casino-spinner" aria-hidden>
      <div className="casino-spinner__ring" />
    </div>
  )
}

function ProviderLogo({
  provider,
  size = 44,
}: {
  provider: Provider
  size?: number
}) {
  const [imgError, setImgError] = useState(false)

  if (provider.logoUrl && !imgError) {
    return (
      <img
        src={provider.logoUrl}
        alt={provider.name}
        loading="lazy"
        onError={() => setImgError(true)}
        style={{ width: size, height: size, objectFit: 'contain', borderRadius: 12, padding: 4, background: 'rgba(255,255,255,0.06)' }}
      />
    )
  }

  return (
    <div className="casino-provider-card__avatar" style={{ width: size, height: size }}>
      {provider.name.slice(0, 2).toUpperCase()}
    </div>
  )
}

function GamePlayerOverlay({
  game,
  onExit,
}: {
  game: ActiveGame
  onExit: () => void
}) {
  const iframeRef = useRef<HTMLIFrameElement>(null)

  const handleIframeLoad = useCallback(() => {
    try {
      const href = iframeRef.current?.contentWindow?.location?.href ?? ''
      if (!href || href === 'about:blank') return
      if (href.startsWith(window.location.origin)) {
        onExit()
      }
    } catch {
      // Ignore cross-origin access while game is running.
    }
  }, [onExit])

  useEffect(() => {
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [])

  return (
    <div className="casino-overlay">
      <div className="casino-overlay__topbar">
        <button type="button" className="casino-overlay__exit" onClick={onExit}>
          <i className="fas fa-arrow-left me-2" />
          ออกจากเกม
        </button>
        <div className="casino-overlay__title">
          {game.providerName}
          {game.gameName && game.gameName !== game.providerName ? (
            <span className="casino-overlay__subtitle"> - {game.gameName}</span>
          ) : null}
        </div>
        <div className="casino-overlay__spacer" />
      </div>

      <div className="casino-overlay__body">
        {game.openedInNewTab ? (
          <div className="casino-overlay__notice">
            <div className="casino-overlay__notice-icon">🎮</div>
            <h3>{game.gameName || game.providerName}</h3>
            <p>เกมถูกเปิดในแท็บใหม่เพื่อหลีกเลี่ยงปัญหา Mixed Content บนหน้า HTTPS</p>
            <div className="casino-overlay__notice-actions">
              <button
                type="button"
                className="casino-btn casino-btn--primary"
                onClick={() => window.open(game.url, '_blank', 'noopener,noreferrer')}
              >
                กลับไปเกม
              </button>
              <button type="button" className="casino-btn casino-btn--ghost" onClick={onExit}>
                ปิดสถานะการเล่น
              </button>
            </div>
          </div>
        ) : (
          <iframe
            ref={iframeRef}
            src={game.url}
            title={game.gameName}
            className="casino-overlay__frame"
            allow="clipboard-read; clipboard-write; fullscreen"
            onLoad={handleIframeLoad}
          />
        )}
      </div>
    </div>
  )
}

const CasinoPage: React.FC = () => {
  const navigate = useNavigate()
  const { refreshWallet } = useWallet()
  const [tenantId, setTenantId] = useState<string | null>(null)
  const [loggedIn, setLoggedIn] = useState(() => casinoService.isLoggedIn())
  const [providers, setProviders] = useState<Provider[]>([])
  const [gamesCache, setGamesCache] = useState<Record<string, Game[]>>({})
  const [activeCategory, setActiveCategory] = useState<CategoryId>('all')
  const [activeProviderId, setActiveProviderId] = useState<string>(ALL_PROVIDERS_ID)
  const [providerSearch, setProviderSearch] = useState('')
  const [gameSearch, setGameSearch] = useState('')
  const [loadingProviders, setLoadingProviders] = useState(false)
  const [loadingGames, setLoadingGames] = useState(false)
  const [providerError, setProviderError] = useState<string | null>(null)
  const [gameError, setGameError] = useState<string | null>(null)
  const [launchError, setLaunchError] = useState<string | null>(null)
  const [tenantError, setTenantError] = useState<string | null>(null)
  const [launchingKey, setLaunchingKey] = useState<string | null>(null)
  const [activeGame, setActiveGame] = useState<ActiveGame | null>(null)
  const [popularGames, setPopularGames] = useState<PopularGame[]>([])
  const [popularLoading, setPopularLoading] = useState(false)
  const [popularMode, setPopularMode] = useState<'alltime' | 'weekly'>('alltime')
  const fetchedProvidersRef = useRef<Set<string>>(new Set())
  const loadingProvidersRef = useRef<Set<string>>(new Set())

  const activeCategoryDef = CATEGORY_DEFS.find((item) => item.id === activeCategory) || CATEGORY_DEFS[0]

  const filteredProviders = useMemo(() => {
    if (activeCategory === 'popular') return providers
    if (!activeCategoryDef.ambCategory) return providers
    return providers.filter((provider) => provider.category?.toUpperCase() === activeCategoryDef.ambCategory)
  }, [activeCategory, activeCategoryDef.ambCategory, providers])

  const visibleProviders = useMemo(() => {
    const query = providerSearch.trim().toLowerCase()
    if (!query) return filteredProviders
    return filteredProviders.filter((provider) => provider.name.toLowerCase().includes(query))
  }, [filteredProviders, providerSearch])

  const activeProvider = useMemo(
    () => providers.find((provider) => provider.providerId === activeProviderId) || null,
    [activeProviderId, providers],
  )

  const displayedGames = useMemo(() => {
    if (activeCategory === 'popular') return []
    if (activeProviderId === ALL_PROVIDERS_ID) return []
    return gamesCache[activeProviderId] || []
  }, [activeCategory, activeProviderId, gamesCache])

  const searchedGames = useMemo(() => {
    const query = gameSearch.trim().toLowerCase()
    if (!query) return displayedGames
    return displayedGames.filter((game) => game.name.toLowerCase().includes(query))
  }, [displayedGames, gameSearch])

  const popularGamesFiltered = useMemo(() => {
    const query = gameSearch.trim().toLowerCase()
    const baseGames = popularGames.filter((game) =>
      providerSearch.trim()
        ? game.providerName.toLowerCase().includes(providerSearch.trim().toLowerCase())
        : true,
    )
    if (!query) return baseGames
    return baseGames.filter(
      (game) =>
        game.gameName.toLowerCase().includes(query) ||
        game.providerName.toLowerCase().includes(query),
    )
  }, [gameSearch, popularGames, providerSearch])

  const providerLookup = useMemo(() => {
    const map = new Map<string, Provider>()
    providers.forEach((provider) => map.set(provider.providerId, provider))
    return map
  }, [providers])

  const loadProviders = useCallback(async () => {
    if (!loggedIn || !tenantId) return
    setLoadingProviders(true)
    setProviderError(null)
    try {
      const data = await casinoService.getProviders(tenantId)
      setProviders(data)
    } catch (error) {
      console.error('Casino provider load error:', error)
      setProviderError('ไม่สามารถโหลดข้อมูลค่ายเกมได้')
    } finally {
      setLoadingProviders(false)
    }
  }, [loggedIn, tenantId])

  const loadProviderGames = useCallback(
    async (providerId: string) => {
      if (!loggedIn || !tenantId || providerId === ALL_PROVIDERS_ID) return
      if (fetchedProvidersRef.current.has(providerId) || loadingProvidersRef.current.has(providerId)) return

      loadingProvidersRef.current.add(providerId)
      setLoadingGames(true)
      setGameError(null)

      try {
        const games = await casinoService.getProviderGames(tenantId, providerId)
        setGamesCache((previous) => ({ ...previous, [providerId]: games }))
        fetchedProvidersRef.current.add(providerId)
      } catch (error) {
        console.error('Casino games load error:', error)
        setGameError('ไม่สามารถโหลดรายการเกมได้')
      } finally {
        loadingProvidersRef.current.delete(providerId)
        setLoadingGames(false)
      }
    },
    [loggedIn, tenantId],
  )

  const refreshCatalog = useCallback(async () => {
    fetchedProvidersRef.current.clear()
    loadingProvidersRef.current.clear()
    setGamesCache({})
    await loadProviders()

    if (activeProviderId !== ALL_PROVIDERS_ID) {
      await loadProviderGames(activeProviderId)
    }
  }, [activeProviderId, loadProviderGames, loadProviders])

  const loadPopularGames = useCallback(async () => {
    if (!loggedIn || !tenantId) return
    setPopularLoading(true)
    try {
      const response = await casinoService.getPopularGames(tenantId, 36, popularMode)
      setPopularGames(response.data)
    } catch (error) {
      console.error('Popular games load error:', error)
    } finally {
      setPopularLoading(false)
    }
  }, [loggedIn, popularMode, tenantId])

  useEffect(() => {
    let cancelled = false

    const bootstrapTenant = async () => {
      try {
        const resolvedTenantId = await resolveTenantId()
        if (!cancelled) {
          setTenantId(resolvedTenantId)
          setTenantError(null)
        }
      } catch (error) {
        console.error('Casino tenant resolve error:', error)
        if (!cancelled) {
          setTenantError('ไม่สามารถระบุ tenant สำหรับหน้า casino ได้')
        }
      }
    }

    bootstrapTenant()

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    loadProviders()
  }, [loadProviders])

  useEffect(() => {
    if (activeProviderId !== ALL_PROVIDERS_ID && activeCategory !== 'popular') {
      loadProviderGames(activeProviderId)
    }
  }, [activeCategory, activeProviderId, loadProviderGames])

  useEffect(() => {
    if (activeProviderId !== ALL_PROVIDERS_ID && !providers.some((provider) => provider.providerId === activeProviderId)) {
      setActiveProviderId(ALL_PROVIDERS_ID)
    }
  }, [activeProviderId, providers])

  useEffect(() => {
    if (activeCategory === 'popular') {
      loadPopularGames()
    }
  }, [activeCategory, loadPopularGames])

  useEffect(() => {
    const onStorage = () => setLoggedIn(casinoService.isLoggedIn())
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  useEffect(() => {
    let isRefreshing = false

    const handleGameControlUpdated = async (event: Event) => {
      const detail = (event as CustomEvent<{ tenantId?: string }>).detail
      if (detail?.tenantId && detail.tenantId !== tenantId) {
        return
      }
      if (isRefreshing) return

      isRefreshing = true
      try {
        await refreshCatalog()
      } finally {
        isRefreshing = false
      }
    }

    window.addEventListener('game_control_updated', handleGameControlUpdated)
    return () => window.removeEventListener('game_control_updated', handleGameControlUpdated)
  }, [refreshCatalog, tenantId])

  const selectCategory = useCallback((categoryId: CategoryId) => {
    setActiveCategory(categoryId)
    setActiveProviderId(ALL_PROVIDERS_ID)
    setProviderSearch('')
    setGameSearch('')
    setGameError(null)
    setLaunchError(null)
  }, [])

  const selectProvider = useCallback((providerId: string) => {
    setActiveProviderId(providerId)
    setGameSearch('')
    setGameError(null)
    setLaunchError(null)
  }, [])

  const handleExitGame = useCallback(() => {
    casinoService.clearGameSession()
    setActiveGame(null)
    void refreshWallet()
  }, [refreshWallet])

  useEffect(() => {
    if (!activeGame) return

    const refreshBalanceAfterReturn = () => {
      void refreshWallet()
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshBalanceAfterReturn()
      }
    }

    window.addEventListener('focus', refreshBalanceAfterReturn)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.removeEventListener('focus', refreshBalanceAfterReturn)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [activeGame, refreshWallet])

  const handlePlayGame = useCallback(
    async (provider: Provider, game: Game) => {
      if (!tenantId) {
        setLaunchError('ไม่สามารถระบุ tenant สำหรับการเข้าเล่นเกมได้')
        return
      }

      if (!loggedIn) {
        navigate('/login')
        return
      }

      const launchKey = `${provider.providerId}:${game.gameCode}`
      if (launchingKey) return

      setLaunchingKey(launchKey)
      setLaunchError(null)

      try {
        const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
        const callbackUrl = `${window.location.origin}/lobby-casino`
        const result = await casinoService.launchGame(tenantId, provider, game, isMobile, callbackUrl)

        casinoService.trackGamePlay(tenantId, game, provider)
        casinoService.setGameSession({
          active: true,
          gameCode: game.gameCode,
          gameName: game.name,
          providerName: provider.name,
          startTime: Date.now(),
        })

        const isHttpGame = result.gameUrl.startsWith('http://')
        const isHttpsPage = window.location.protocol === 'https:'
        if (isHttpGame && isHttpsPage) {
          window.open(result.gameUrl, '_blank', 'noopener,noreferrer')
          setActiveGame({
            url: result.gameUrl,
            gameName: game.name,
            providerName: provider.name,
            gameCode: game.gameCode,
            providerId: provider.providerId,
            openedInNewTab: true,
          })
          return
        }

        setActiveGame({
          url: result.gameUrl,
          gameName: game.name,
          providerName: provider.name,
          gameCode: game.gameCode,
          providerId: provider.providerId,
        })
      } catch (error) {
        console.error('Launch game error:', error)
        setLaunchError(error instanceof Error ? error.message : 'ไม่สามารถเปิดเกมได้')
      } finally {
        setLaunchingKey(null)
      }
    },
    [launchingKey, loggedIn, navigate, tenantId],
  )

  const renderProviderDirectory = () => {
    if (loadingProviders) {
      return (
        <div className="casino-state">
          <Spinner />
          <div>กำลังโหลดค่ายเกม...</div>
        </div>
      )
    }

    if (providerError) {
      return (
        <div className="casino-state casino-state--error">
          <div>{providerError}</div>
          <button type="button" className="casino-btn casino-btn--primary" onClick={loadProviders}>
            ลองใหม่
          </button>
        </div>
      )
    }

    if (visibleProviders.length === 0) {
      return (
        <div className="casino-state">
          <div className="casino-state__emoji">🎰</div>
          <div>ไม่พบค่ายเกมในหมวดนี้</div>
        </div>
      )
    }

    return (
      <div className="casino-provider-grid">
        {visibleProviders.map((provider) => (
          <button
            key={provider.providerId}
            type="button"
            className="casino-provider-card"
            onClick={() => selectProvider(provider.providerId)}
          >
            <ProviderLogo provider={provider} />
            <div className="casino-provider-card__body">
              <div className="casino-provider-card__name">{provider.name}</div>
              <div className="casino-provider-card__meta">
                {provider.category || (provider.platform === 'rb7' ? 'RB7' : 'เกมส์')}
              </div>
              <div className="casino-provider-card__count">{provider.gameCount || 0} เกมส์</div>
            </div>
          </button>
        ))}
      </div>
    )
  }

  const renderPopularGames = () => {
    if (popularLoading) {
      return (
        <div className="casino-state">
          <Spinner />
          <div>กำลังโหลดเกมยอดนิยม...</div>
        </div>
      )
    }

    if (popularGamesFiltered.length === 0) {
      return (
        <div className="casino-state">
          <div className="casino-state__emoji">🔥</div>
          <div>ยังไม่มีข้อมูลเกมยอดนิยม</div>
        </div>
      )
    }

    return (
      <div className="casino-games-grid">
        {popularGamesFiltered.map((game) => {
          const provider = providerLookup.get(game.providerId)
          if (!provider) return null
          const key = `${provider.providerId}:${game.gameCode}`
          const isLaunching = launchingKey === key

          return (
            <div key={`${game.providerId}:${game.gameCode}`} className="casino-game-card">
              <div className="casino-game-card__image-wrap">
                <img
                  src={game.imageUrl || PLACEHOLDER_IMAGE}
                  alt={game.gameName}
                  loading="lazy"
                  className="casino-game-card__image"
                  onError={(event) => {
                    ;(event.currentTarget as HTMLImageElement).src = PLACEHOLDER_IMAGE
                  }}
                />
                <div className="casino-game-card__badge">
                  <i className="fas fa-fire me-1" />
                  #{game.rank}
                </div>
              </div>
              <div className="casino-game-card__content">
                <div className="casino-game-card__title">{game.gameName}</div>
                <div className="casino-game-card__provider">{game.providerName}</div>
                <div className="casino-game-card__stats">
                  เล่น {popularMode === 'weekly' ? game.weeklyCount || game.count : game.playCount || game.count} ครั้ง
                </div>
                <button
                  type="button"
                  className="casino-btn casino-btn--primary"
                  disabled={isLaunching}
                  onClick={() =>
                    handlePlayGame(provider, {
                      gameCode: game.gameCode,
                      name: game.gameName,
                      imageUrl: game.imageUrl,
                      isEnabled: true,
                    })
                  }
                >
                  {isLaunching ? 'กำลังเปิด...' : 'เข้าเล่น'}
                </button>
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  const renderGamesGrid = () => {
    if (activeCategory === 'popular') {
      return renderPopularGames()
    }

    if (activeProviderId === ALL_PROVIDERS_ID) {
      return renderProviderDirectory()
    }

    if (loadingGames && displayedGames.length === 0) {
      return (
        <div className="casino-state">
          <Spinner />
          <div>กำลังโหลดรายการเกม...</div>
        </div>
      )
    }

    if (gameError) {
      return (
        <div className="casino-state casino-state--error">
          <div>{gameError}</div>
          <button
            type="button"
            className="casino-btn casino-btn--primary"
            onClick={() => loadProviderGames(activeProviderId)}
          >
            โหลดอีกครั้ง
          </button>
        </div>
      )
    }

    if (searchedGames.length === 0) {
      return (
        <div className="casino-state">
          <div className="casino-state__emoji">🎮</div>
          <div>ไม่พบเกมที่ค้นหา</div>
        </div>
      )
    }

    return (
      <div className="casino-games-grid">
        {searchedGames.map((game) => {
          const key = `${activeProvider?.providerId || 'unknown'}:${game.gameCode}`
          const isLaunching = launchingKey === key

          return (
            <div key={game.gameCode} className="casino-game-card">
              <div className="casino-game-card__image-wrap">
                <img
                  src={game.imageUrl || PLACEHOLDER_IMAGE}
                  alt={game.name}
                  loading="lazy"
                  className="casino-game-card__image"
                  onError={(event) => {
                    ;(event.currentTarget as HTMLImageElement).src = PLACEHOLDER_IMAGE
                  }}
                />
                {typeof game.rank === 'number' && game.rank > 0 && game.rank <= 10 ? (
                  <div className="casino-game-card__badge casino-game-card__badge--catalog">
                    TOP {game.rank}
                  </div>
                ) : null}
              </div>
              <div className="casino-game-card__content">
                <div className="casino-game-card__title">{game.name}</div>
                <div className="casino-game-card__provider">{activeProvider?.name || 'Casino'}</div>
                <button
                  type="button"
                  className="casino-btn casino-btn--primary"
                  disabled={isLaunching || !activeProvider}
                  onClick={() => activeProvider && handlePlayGame(activeProvider, game)}
                >
                  {isLaunching ? 'กำลังเปิด...' : 'เข้าเล่น'}
                </button>
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <>
      <style>{`
        .casino-lobby {
          color: #fff;
        }
        .casino-shell {
          background:
            radial-gradient(circle at top right, rgba(59,130,246,.18), transparent 28%),
            radial-gradient(circle at bottom left, rgba(34,197,94,.12), transparent 24%),
            linear-gradient(180deg, #081124 0%, #09162e 48%, #07111f 100%);
          border: 1px solid rgba(255,255,255,.08);
          border-radius: 18px;
          box-shadow: 0 20px 50px rgba(0,0,0,.32);
          overflow: hidden;
        }
        .casino-hero {
          padding: 18px;
          border-bottom: 1px solid rgba(255,255,255,.08);
          background: linear-gradient(135deg, rgba(37,99,235,.22), rgba(15,23,42,.85));
        }
        .casino-hero__top {
          display: flex;
          justify-content: space-between;
          gap: 12px;
          align-items: center;
          flex-wrap: wrap;
        }
        .casino-hero__title {
          font-size: 24px;
          font-weight: 700;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .casino-hero__subtitle {
          margin-top: 8px;
          color: rgba(255,255,255,.72);
          font-size: 14px;
        }
        .casino-summary {
          margin-top: 14px;
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 10px;
        }
        .casino-summary__card {
          background: rgba(255,255,255,.06);
          border: 1px solid rgba(255,255,255,.08);
          border-radius: 14px;
          padding: 12px;
        }
        .casino-summary__label {
          font-size: 12px;
          text-transform: uppercase;
          letter-spacing: .04em;
          color: rgba(255,255,255,.6);
        }
        .casino-summary__value {
          margin-top: 4px;
          font-size: 18px;
          font-weight: 700;
        }
        .casino-toolbar {
          padding: 14px 18px 10px;
          display: grid;
          gap: 12px;
          border-bottom: 1px solid rgba(255,255,255,.06);
          background: rgba(3,7,18,.26);
        }
        .casino-category-row,
        .casino-provider-row {
          display: flex;
          gap: 10px;
          overflow-x: auto;
          padding-bottom: 4px;
          scrollbar-width: thin;
        }
        .casino-category-row::-webkit-scrollbar,
        .casino-provider-row::-webkit-scrollbar {
          height: 6px;
        }
        .casino-category-row::-webkit-scrollbar-thumb,
        .casino-provider-row::-webkit-scrollbar-thumb {
          background: rgba(148,163,184,.35);
          border-radius: 999px;
        }
        .casino-chip {
          border: 1px solid rgba(255,255,255,.12);
          background: rgba(15,23,42,.92);
          color: rgba(255,255,255,.78);
          border-radius: 999px;
          padding: 10px 14px;
          min-width: max-content;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          transition: all .2s ease;
        }
        .casino-chip.is-active {
          background: linear-gradient(135deg, #2563eb, #0ea5e9);
          color: #fff;
          border-color: transparent;
          box-shadow: 0 10px 20px rgba(37,99,235,.28);
        }
        .casino-chip--provider {
          padding: 9px 12px;
        }
        .casino-chip__count {
          opacity: .72;
          font-size: 12px;
        }
        .casino-toolbar__searches {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
        }
        .casino-search {
          position: relative;
        }
        .casino-search i {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: rgba(255,255,255,.45);
        }
        .casino-search input {
          width: 100%;
          border-radius: 12px;
          border: 1px solid rgba(255,255,255,.1);
          background: rgba(15,23,42,.88);
          color: #fff;
          padding: 11px 12px 11px 36px;
          outline: none;
        }
        .casino-search input::placeholder {
          color: rgba(255,255,255,.42);
        }
        .casino-content {
          padding: 18px;
        }
        .casino-content__header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
          margin-bottom: 14px;
          flex-wrap: wrap;
        }
        .casino-content__title {
          font-size: 18px;
          font-weight: 700;
        }
        .casino-content__meta {
          color: rgba(255,255,255,.58);
          font-size: 13px;
        }
        .casino-segmented {
          display: inline-flex;
          background: rgba(15,23,42,.88);
          border-radius: 999px;
          padding: 4px;
          gap: 4px;
        }
        .casino-segmented button {
          border: 0;
          background: transparent;
          color: rgba(255,255,255,.6);
          border-radius: 999px;
          padding: 8px 12px;
          cursor: pointer;
        }
        .casino-segmented button.is-active {
          background: linear-gradient(135deg, #f59e0b, #ef4444);
          color: #fff;
        }
        .casino-provider-grid,
        .casino-games-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
          gap: 14px;
        }
        .casino-provider-card,
        .casino-game-card {
          border: 1px solid rgba(255,255,255,.08);
          background: rgba(10,16,31,.88);
          border-radius: 16px;
          overflow: hidden;
          transition: transform .2s ease, box-shadow .2s ease, border-color .2s ease;
        }
        .casino-provider-card:hover,
        .casino-game-card:hover {
          transform: translateY(-2px);
          border-color: rgba(96,165,250,.45);
          box-shadow: 0 14px 28px rgba(0,0,0,.24);
        }
        .casino-provider-card {
          text-align: left;
          display: grid;
          grid-template-columns: auto 1fr;
          gap: 12px;
          padding: 14px;
          align-items: start;
        }
        .casino-provider-card__avatar {
          width: 48px;
          height: 48px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 800;
          background: linear-gradient(135deg, #1d4ed8, #0ea5e9);
        }
        .casino-provider-card__body {
          display: flex;
          flex-direction: column;
          gap: 4px;
          min-width: 0;
        }
        .casino-provider-card__name {
          font-weight: 700;
          color: #fff;
        }
        .casino-provider-card__meta,
        .casino-provider-card__count,
        .casino-game-card__provider,
        .casino-game-card__stats {
          color: rgba(255,255,255,.58);
          font-size: 12px;
        }
        .casino-provider-card__count {
          margin-top: 6px;
          color: rgba(255,255,255,.72);
        }
        .casino-game-card__image-wrap {
          position: relative;
          aspect-ratio: 3 / 4;
          background:
            radial-gradient(circle at top, rgba(59,130,246,.12), transparent 48%),
            rgba(255,255,255,.04);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 10px;
        }
        .casino-game-card__image {
          width: 100%;
          height: 100%;
          object-fit: contain;
          object-position: center;
          display: block;
          border-radius: 12px;
        }
        .casino-game-card__badge {
          position: absolute;
          top: 10px;
          left: 10px;
          border-radius: 999px;
          padding: 6px 10px;
          background: rgba(0,0,0,.72);
          color: #fff;
          font-size: 12px;
          font-weight: 700;
        }
        .casino-game-card__badge--catalog {
          left: auto;
          right: 10px;
          background: #facc15;
          color: #713f12;
        }
        .casino-game-card__content {
          padding: 12px;
          display: grid;
          gap: 8px;
        }
        .casino-game-card__title {
          font-weight: 700;
          color: #fff;
          min-height: 44px;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .casino-btn {
          border: 0;
          border-radius: 12px;
          padding: 10px 12px;
          cursor: pointer;
          font-weight: 700;
          transition: opacity .2s ease, transform .2s ease;
        }
        .casino-btn:disabled {
          opacity: .6;
          cursor: wait;
        }
        .casino-btn--primary {
          color: #fff;
          background: linear-gradient(135deg, #2563eb, #0ea5e9);
        }
        .casino-btn--ghost {
          color: #fff;
          border: 1px solid rgba(255,255,255,.14);
          background: rgba(15,23,42,.9);
        }
        .casino-state {
          min-height: 220px;
          display: grid;
          place-items: center;
          text-align: center;
          gap: 10px;
          color: rgba(255,255,255,.72);
        }
        .casino-state--error {
          color: #fca5a5;
        }
        .casino-state__emoji {
          font-size: 42px;
        }
        .casino-spinner {
          width: 42px;
          height: 42px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        .casino-spinner__ring {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          border: 3px solid rgba(255,255,255,.18);
          border-top-color: #38bdf8;
          animation: casino-spin 1s linear infinite;
        }
        .casino-overlay {
          position: fixed;
          inset: 0;
          z-index: 9999;
          background: #000;
          display: flex;
          flex-direction: column;
        }
        .casino-overlay__topbar {
          min-height: 52px;
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 14px;
          background: rgba(2,6,23,.95);
          border-bottom: 1px solid rgba(255,255,255,.08);
        }
        .casino-overlay__exit {
          border: 1px solid rgba(248,113,113,.5);
          background: rgba(127,29,29,.24);
          color: #fecaca;
          border-radius: 10px;
          padding: 8px 12px;
          font-weight: 700;
          cursor: pointer;
        }
        .casino-overlay__title {
          flex: 1;
          text-align: center;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          color: #fff;
          font-weight: 700;
        }
        .casino-overlay__subtitle {
          color: rgba(255,255,255,.58);
          font-weight: 400;
        }
        .casino-overlay__spacer {
          width: 124px;
        }
        .casino-overlay__body {
          flex: 1;
          position: relative;
        }
        .casino-overlay__frame {
          width: 100%;
          height: 100%;
          border: 0;
        }
        .casino-overlay__notice {
          height: 100%;
          display: grid;
          place-items: center;
          align-content: center;
          text-align: center;
          gap: 16px;
          padding: 24px;
          color: #fff;
          background: linear-gradient(135deg, #0f172a 0%, #172554 45%, #0f172a 100%);
        }
        .casino-overlay__notice-icon {
          font-size: 60px;
        }
        .casino-overlay__notice-actions {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
          justify-content: center;
        }
        @keyframes casino-spin {
          to { transform: rotate(360deg); }
        }
        @media (max-width: 767px) {
          .casino-summary {
            grid-template-columns: 1fr;
          }
          .casino-toolbar__searches {
            grid-template-columns: 1fr;
          }
          .casino-provider-grid,
          .casino-games-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
          .casino-provider-card {
            grid-template-columns: 1fr;
            text-align: center;
          }
          .casino-provider-card__avatar {
            margin: 0 auto;
          }
          .casino-provider-card__body {
            align-items: center;
          }
          .casino-overlay__spacer {
            display: none;
          }
          .casino-overlay__topbar {
            flex-wrap: wrap;
          }
          .casino-overlay__title {
            order: 3;
            width: 100%;
          }
        }
      `}</style>

      {activeGame ? <GamePlayerOverlay game={activeGame} onExit={handleExitGame} /> : null}

      <div className="content pb-3 casino-lobby">
        <div className="container">
          <div className="casino-shell">
            <div className="casino-hero">
              <div className="casino-hero__top">
                <div>
                  <div className="casino-hero__title">
                    <i className={`${activeCategoryDef.icon}`} />
                    {activeProvider ? activeProvider.name : 'ศูนย์รวมเกมส์ คาสิโน'}
                  </div>
                  <div className="casino-hero__subtitle">
                    ออกแบบใหม่ให้ค้นหาค่ายและเกมได้ง่าย รองรับ provider จำนวนมาก และเปิดเกมจาก API จริง
                  </div>
                </div>
                <button type="button" className="casino-btn casino-btn--ghost" onClick={() => navigate('/dashboard')}>
                  <i className="fas fa-chevron-left me-2" />
                  กลับหน้าหลัก
                </button>
              </div>

              <div className="casino-summary">
                <div className="casino-summary__card">
                  <div className="casino-summary__label">หมวดที่เลือก</div>
                  <div className="casino-summary__value">{activeCategoryDef.label}</div>
                </div>
                <div className="casino-summary__card">
                  <div className="casino-summary__label">จำนวนค่าย</div>
                  <div className="casino-summary__value">{filteredProviders.length}</div>
                </div>
                <div className="casino-summary__card">
                  <div className="casino-summary__label">ค่าย / เกม</div>
                  <div className="casino-summary__value">
                    {activeProvider ? `${searchedGames.length} เกม` : 'เลือกค่ายเพื่อดูเกม'}
                  </div>
                </div>
              </div>
            </div>

            <div className="casino-toolbar">
              <div className="casino-category-row">
                {CATEGORY_DEFS.map((category) => (
                  <button
                    key={category.id}
                    type="button"
                    className={`casino-chip ${activeCategory === category.id ? 'is-active' : ''}`}
                    onClick={() => selectCategory(category.id)}
                  >
                    <i className={category.icon} />
                    {category.label}
                  </button>
                ))}
              </div>

              <div className="casino-toolbar__searches">
                <label className="casino-search">
                  <i className="fas fa-search" />
                  <input
                    value={providerSearch}
                    onChange={(event) => setProviderSearch(event.target.value)}
                    placeholder="ค้นหาค่ายเกม"
                  />
                </label>
                <label className="casino-search">
                  <i className="fas fa-search" />
                  <input
                    value={gameSearch}
                    onChange={(event) => setGameSearch(event.target.value)}
                    placeholder={activeProvider || activeCategory === 'popular' ? 'ค้นหาเกม' : 'ค้นหาเกมภายหลังเมื่อเลือกค่าย'}
                  />
                </label>
              </div>

              {activeCategory !== 'popular' ? (
                <div className="casino-provider-row">
                  <button
                    type="button"
                    className={`casino-chip casino-chip--provider ${activeProviderId === ALL_PROVIDERS_ID ? 'is-active' : ''}`}
                    onClick={() => selectProvider(ALL_PROVIDERS_ID)}
                  >
                    <i className="fas fa-layer-group" />
                    รวมทุกค่าย
                    <span className="casino-chip__count">{visibleProviders.length}</span>
                  </button>

                  {visibleProviders.map((provider) => (
                    <button
                      key={provider.providerId}
                      type="button"
                      className={`casino-chip casino-chip--provider ${activeProviderId === provider.providerId ? 'is-active' : ''}`}
                      onClick={() => selectProvider(provider.providerId)}
                    >
                    {provider.logoUrl ? (
                      <img
                        src={provider.logoUrl}
                        alt={provider.name}
                        style={{ width: 18, height: 18, objectFit: 'contain', borderRadius: 6, background: 'rgba(255,255,255,.08)', padding: 2 }}
                        onError={(event) => { (event.currentTarget as HTMLImageElement).style.display = 'none' }}
                      />
                    ) : null}
                      {provider.name}
                      <span className="casino-chip__count">{provider.gameCount || 0}</span>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="casino-content__header">
                  <div className="casino-content__meta">จัดอันดับจากสถิติการเล่นจริง</div>
                  <div className="casino-segmented">
                    <button
                      type="button"
                      className={popularMode === 'alltime' ? 'is-active' : ''}
                      onClick={() => setPopularMode('alltime')}
                    >
                      ตลอดเวลา
                    </button>
                    <button
                      type="button"
                      className={popularMode === 'weekly' ? 'is-active' : ''}
                      onClick={() => setPopularMode('weekly')}
                    >
                      7 วันล่าสุด
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="casino-content">
              <div className="casino-content__header">
                <div>
                  <div className="casino-content__title">
                    {activeCategory === 'popular'
                      ? 'เกมยอดนิยม'
                      : activeProvider
                        ? `รายการเกม ${activeProvider.name}`
                        : 'เลือกค่ายเกมที่ต้องการเข้าเล่น'}
                  </div>
                  <div className="casino-content__meta">
                    {launchError || providerError || gameError || (loggedIn ? 'รองรับหลายค่าย หลายหมวด และค้นหาได้รวดเร็ว' : 'กรุณาเข้าสู่ระบบก่อนเข้าเล่น')}
                  </div>
                </div>

                {activeProvider ? (
                  <button
                    type="button"
                    className="casino-btn casino-btn--ghost"
                    onClick={() => selectProvider(ALL_PROVIDERS_ID)}
                  >
                    <i className="fas fa-th-large me-2" />
                    ดูค่ายทั้งหมด
                  </button>
                ) : null}
              </div>

              {!loggedIn ? (
                <div className="casino-state">
                  <div className="casino-state__emoji">🔐</div>
                  <div>กรุณาเข้าสู่ระบบก่อนเข้าเล่นคาสิโน</div>
                  <button type="button" className="casino-btn casino-btn--primary" onClick={() => navigate('/login')}>
                    ไปหน้าเข้าสู่ระบบ
                  </button>
                </div>
              ) : tenantError ? (
                <div className="casino-state casino-state--error">
                  <div>{tenantError}</div>
                  <button type="button" className="casino-btn casino-btn--primary" onClick={() => window.location.reload()}>
                    โหลดหน้าใหม่
                  </button>
                </div>
              ) : !tenantId ? (
                <div className="casino-state">
                  <Spinner />
                  <div>กำลังเตรียม tenant สำหรับหน้า casino...</div>
                </div>
              ) : (
                renderGamesGrid()
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default CasinoPage
